import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Banknote, ChevronDown, RefreshCw } from "lucide-react";

const fmt = (n) => (Number(n) || 0).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Barra compacta de tasas del día (BCV, Euro, USDT). Reutilizable en toda la app.
export function RatesBar() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const load = (force = false) => {
    setLoading(true);
    api.get("/rates", { params: force ? { t: Date.now() } : {} })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const usd = data?.assets?.find((a) => a.code === "usd_bcv");

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs font-mono2 border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 rounded-lg px-2.5 py-1.5 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition"
        data-testid="rates-bar-toggle"
        title="Tasas de cambio del día"
      >
        <Banknote size={14} />
        {usd ? `$ ${fmt(usd.rate)} Bs` : "Tasas"}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-40 mt-1 right-0 w-60 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-3 shadow-xl" data-testid="rates-bar-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="font-heading font-medium text-sm">Tasas del día</p>
            <button onClick={() => load(true)} className="text-slate-400 hover:text-emerald-500" title="Actualizar" data-testid="rates-refresh">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
          {!data ? (
            <p className="font-mono2 text-[11px] text-slate-400">Cargando...</p>
          ) : (
            <>
              {data.assets.map((a) => (
                <div key={a.code} className="flex items-center justify-between py-1 text-xs" data-testid={`rate-${a.code}`}>
                  <span className="text-slate-500 dark:text-slate-400">{a.label}</span>
                  <span className="font-mono2 font-medium">{fmt(a.rate)} Bs</span>
                </div>
              ))}
              <p className="font-mono2 text-[10px] text-slate-400 mt-2">
                {data.updated ? `Actualizado: ${new Date(data.updated).toLocaleString("es-VE")}` : ""}
              </p>
              <p className="font-mono2 text-[9px] text-slate-400 leading-tight mt-0.5">{data.source}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
